'use client';

import React, { Component, ReactNode } from 'react';
import Icon from './ui/AppIcon';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  retryCount: number;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for debugging — no sensitive data exposed
    console.error('[ErrorBoundary] Caught error:', error.message);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    this.setState({ error, errorInfo });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prev.retryCount + 1,
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="min-h-[400px] flex items-center justify-center px-4 py-16"
          style={{ background: 'var(--bg-primary)' }}
        >
          <div
            className="max-w-lg w-full rounded-2xl p-8 text-center"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
          >
            {/* Icon */}
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ background: '#EF444418' }}
            >
              <Icon name="ExclamationTriangleIcon" size={32} style={{ color: '#EF4444' } as React.CSSProperties} />
            </div>

            {/* Title */}
            <h2
              className="font-display font-medium text-2xl mb-3"
              style={{ color: 'var(--text-primary)' }}
            >
              Something went wrong
            </h2>

            {/* Description */}
            <p
              className="text-sm mb-6 max-w-sm mx-auto"
              style={{ color: 'var(--text-secondary)' }}
            >
              We encountered an unexpected error{this.props.componentName ? ` in ${this.props.componentName}` : ''}. Your data is safe. Please try again.
            </p>

            {/* Dev-only error details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div
                className="mb-6 p-4 rounded-xl text-left"
                style={{ background: '#EF444408', border: '1px solid #EF444430' }}
              >
                <p className="text-xs font-mono" style={{ color: '#EF4444' }}>
                  {this.state.error.message}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="btn-primary px-6 py-2.5"
              >
                <Icon name="ArrowPathIcon" size={16} />
                Retry
              </button>
              <button
                onClick={this.handleReload}
                className="btn-secondary px-6 py-2.5"
              >
                Reload Page
              </button>
              <a
                href="/homepage"
                className="btn-secondary px-6 py-2.5"
              >
                <Icon name="HomeIcon" size={16} />
                Go Home
              </a>
            </div>

            {this.state.retryCount > 0 && (
              <p className="text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
                Retry attempt {this.state.retryCount}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;